import { useState, useEffect } from 'react';
import {
  Upload,
  Download,
  RotateCcw,
  Sparkles,
  Loader2,
  XCircle,
  Layers,
} from 'lucide-react';
import { toast } from 'sonner';
import AppShellPage from '@components/layout/AppShellPage.js';
import Button from '@components/ui/Button.js';
import SurfaceCard from '@components/ui/SurfaceCard.js';
import { cn } from '@utils/Cn.js';
import {
  bulkImportService,
  useIntrospectFileMutation,
  usePreviewMappedMutation,
  useImportMappedMutation,
  FileUploadZone,
  ColumnMappingWizard,
  DataReviewGrid,
  ImportSummaryCard,
  getSavedPresets,
  savePreset,
  findMatchingPreset,
  type FileIntrospectResult,
  type ColumnMappingConfig,
  type DefaultValuesConfig,
  type BulkImportResult,
  type ImportPhase,
  type ImportMappingPreset,
} from '@features/bulkImport/index.js';

const INITIAL_MAPPING: ColumnMappingConfig = {
  insuredName: '',
  mobileNumber: '',
  endDate: '',
  policyTypeName: '',
  vehicleNumber: '',
  policyNumber: '',
  referenceName: '',
  premiumPrice: '',
  referenceNote: '',
  clientName: '',
  outsourceAgentName: '',
  outsourceAgentPhone: '',
  splitVehicleAndPolicyType: true,
  policyTypeValueMappings: {},
};

const INITIAL_DEFAULTS: DefaultValuesConfig = {
  policyTypeName: 'Motor',
  referenceName: '',
  premiumPrice: null,
};

export default function BulkImportPage() {
  const [phase, setPhase] = useState<ImportPhase>('upload');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [introspectResult, setIntrospectResult] = useState<FileIntrospectResult | null>(null);
  const [selectedSheet, setSelectedSheet] = useState<string>('');
  const [headerRowIndex, setHeaderRowIndex] = useState<number>(0);
  const [columnMapping, setColumnMapping] = useState<ColumnMappingConfig>(INITIAL_MAPPING);
  const [defaultValues, setDefaultValues] = useState<DefaultValuesConfig>(INITIAL_DEFAULTS);
  const [overrides, setOverrides] = useState<Record<number, Record<string, unknown>>>({});
  const [previewResult, setPreviewResult] = useState<BulkImportResult | null>(null);
  const [commitResult, setCommitResult] = useState<BulkImportResult | null>(null);
  const [errorDetails, setErrorDetails] = useState<string[]>([]);
  const [savedPresets, setSavedPresets] = useState<ImportMappingPreset[]>([]);
  const [matchedPreset, setMatchedPreset] = useState<ImportMappingPreset | null>(null);

  const introspectMutation = useIntrospectFileMutation();
  const previewMutation = usePreviewMappedMutation();
  const importMutation = useImportMappedMutation();

  useEffect(() => {
    setSavedPresets(getSavedPresets());
  }, []);

  // ─── Download Template ──────────────────────────────────────────────────────
  function handleDownloadTemplate() {
    const toastId = toast.loading('Downloading template...');
    bulkImportService
      .downloadTemplate()
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'import-template.xlsx';
        a.click();
        URL.revokeObjectURL(url);
        toast.success('Template downloaded successfully', { id: toastId });
      })
      .catch(() => {
        toast.error('Failed to download template', { id: toastId });
      });
  }

  // ─── Step 1: File Selected → Introspect ─────────────────────────────────────
  function handleFileSelected(file: File) {
    setSelectedFile(file);
    setIntrospectResult(null);
    setPreviewResult(null);
    setCommitResult(null);
    setOverrides({});
    setErrorDetails([]);

    introspectMutation.mutate(file, {
      onSuccess: (res) => {
        if (res.success && res.data) {
          const data = res.data;
          setIntrospectResult(data);
          setSelectedSheet(data.selectedSheet);
          setHeaderRowIndex(data.detectedHeaderRowIndex);

          // Check if there is a matching saved preset
          const preset = findMatchingPreset(data.headers);
          if (preset) {
            setMatchedPreset(preset);
            const reconciled: ColumnMappingConfig = {
              ...INITIAL_MAPPING,
              ...preset.mapping,
              splitVehicleAndPolicyType: preset.mapping.splitVehicleAndPolicyType ?? true,
              policyTypeValueMappings: preset.policyTypeValueMappings ?? preset.mapping.policyTypeValueMappings ?? {},
            };
            const targetKeys = [
              'insuredName',
              'mobileNumber',
              'endDate',
              'vehicleNumber',
              'policyNumber',
              'referenceName',
              'referenceNote',
              'clientName',
              'outsourceAgentName',
              'outsourceAgentPhone',
              'policyTypeName',
              'premiumPrice',
            ] as const;

            for (const key of targetKeys) {
              const currentVal = reconciled[key];
              const headerMatch = data.headers.find(
                (h) =>
                  h === currentVal ||
                  h.trim().toUpperCase() === currentVal?.trim().toUpperCase() ||
                  h.toLowerCase().replace(/[^a-z0-9]/g, '') === currentVal?.toLowerCase().replace(/[^a-z0-9]/g, ''),
              );
              if (headerMatch) {
                reconciled[key] = headerMatch;
              } else {
                const sugMatch = Object.entries(data.suggestedMapping).find(
                  ([, s]) => s.targetKey === key && s.confidence >= 50,
                );
                if (sugMatch && data.headers.includes(sugMatch[0])) {
                  reconciled[key] = sugMatch[0];
                } else {
                  reconciled[key] = '';
                }
              }
            }

            if (data.detectedMultiYearColumns && data.detectedMultiYearColumns.length > 0) {
              reconciled.policyNumberYears = data.detectedMultiYearColumns;
            }

            setColumnMapping(reconciled);
            if (preset.defaultValues) {
              setDefaultValues({
                ...INITIAL_DEFAULTS,
                ...preset.defaultValues,
              });
            }
            toast.success(`Recognized template: "${preset.name}" applied automatically!`);
          } else {
            // Apply auto-suggested mappings from introspect (highest confidence per target key)
            const newMapping: ColumnMappingConfig = {
              ...INITIAL_MAPPING,
              splitVehicleAndPolicyType: data.hasDualVehicleTypeColumn ?? true,
              policyTypeValueMappings: {},
            };

            const bestConfidence: Record<string, number> = {};
            for (const [colName, sug] of Object.entries(data.suggestedMapping)) {
              if (sug.targetKey !== 'ignore' && sug.targetKey !== 'policyNumberYears') {
                const currentBest = bestConfidence[sug.targetKey] ?? 0;
                if (sug.confidence >= currentBest && data.headers.includes(colName)) {
                  bestConfidence[sug.targetKey] = sug.confidence;
                  (newMapping as unknown as Record<string, string>)[sug.targetKey] = colName;
                }
              }
            }

            if (data.detectedMultiYearColumns && data.detectedMultiYearColumns.length > 0) {
              newMapping.policyNumberYears = data.detectedMultiYearColumns;
            }

            setColumnMapping(newMapping);
          }
        } else if (res.error) {
          setErrorDetails(res.error.details ?? [res.error.message]);
          setPhase('error');
          toast.error(res.error.message);
        }
      },
      onError: (err) => {
        const axErr = err as { response?: { data?: { error?: { message?: string; details?: string[] } } }; message?: string };
        const srvErr = axErr.response?.data?.error;
        setErrorDetails(srvErr?.details ?? [srvErr?.message ?? axErr.message ?? 'File analysis failed']);
        setPhase('error');
      },
    });
  }

  // ─── Step 2: Proceed to Column Mapping ──────────────────────────────────────
  function handleProceedToMapping() {
    setPhase('mapping');
  }

  // ─── Step 3: Run Dry-Run Preview with Current Mapping ───────────────────────
  function handleRunPreview(customMapping?: ColumnMappingConfig) {
    if (!introspectResult && !selectedFile) return;

    const activeMapping = customMapping ?? columnMapping;

    if (!activeMapping.insuredName || !activeMapping.mobileNumber || !activeMapping.endDate) {
      toast.error('Please map all required fields (Insured Name, Mobile Number, Expiry Date) before proceeding');
      return;
    }

    if (customMapping) {
      setColumnMapping(customMapping);
    }

    setPhase('previewing');

    previewMutation.mutate(
      {
        fileId: introspectResult?.fileId,
        file: introspectResult?.fileId ? undefined : selectedFile ?? undefined,
        sheetName: selectedSheet,
        headerRowIndex,
        columnMapping: activeMapping,
        defaultValues,
        overrides,
      },
      {
        onSuccess: (res) => {
          if (res.success && res.data) {
            setPreviewResult(res.data);
            setPhase('review');
            if (res.data.failedCount > 0) {
              toast.warning(`${String(res.data.failedCount)} rows need attention before importing`);
            } else {
              toast.success(`All ${String(res.data.totalRows)} rows ready to import!`);
            }
          } else if (res.error) {
            setErrorDetails(res.error.details ?? [res.error.message]);
            setPhase('error');
            toast.error(res.error.message);
          }
        },
        onError: (err) => {
          const axErr = err as { response?: { data?: { error?: { message?: string; details?: string[] } } }; message?: string };
          const srvErr = axErr.response?.data?.error;
          setErrorDetails(srvErr?.details ?? [srvErr?.message ?? axErr.message ?? 'Preview validation failed']);
          setPhase('error');
        },
      },
    );
  }

  // ─── In-cell Override Handler ──────────────────────────────────────────────
  function handleRowOverride(rowNumber: number, field: string, value: string) {
    const nextOverrides = {
      ...overrides,
      [rowNumber]: {
        ...(overrides[rowNumber] ?? {}),
        [field]: value,
      },
    };
    setOverrides(nextOverrides);

    // Re-run fast preview validation in background to update grid badges
    previewMutation.mutate(
      {
        fileId: introspectResult?.fileId,
        file: introspectResult?.fileId ? undefined : selectedFile ?? undefined,
        sheetName: selectedSheet,
        headerRowIndex,
        columnMapping,
        defaultValues,
        overrides: nextOverrides,
      },
      {
        onSuccess: (res) => {
          if (res.success && res.data) {
            setPreviewResult(res.data);
            toast.success(`Row ${String(rowNumber)} updated and re-validated!`);
          }
        },
      },
    );
  }

  // ─── Multi-field Override Handler (from modal) ─────────────────────────────
  function handleRowMultiOverride(rowNumber: number, updates: Record<string, unknown>) {
    const nextOverrides = {
      ...overrides,
      [rowNumber]: {
        ...(overrides[rowNumber] ?? {}),
        ...updates,
      },
    };
    setOverrides(nextOverrides);

    previewMutation.mutate(
      {
        fileId: introspectResult?.fileId,
        file: introspectResult?.fileId ? undefined : selectedFile ?? undefined,
        sheetName: selectedSheet,
        headerRowIndex,
        columnMapping,
        defaultValues,
        overrides: nextOverrides,
      },
      {
        onSuccess: (res) => {
          if (res.success && res.data) {
            setPreviewResult(res.data);
            toast.success(`Row ${String(rowNumber)} updated and re-validated!`);
          }
        },
      },
    );
  }

  // ─── Batch Resolve Issues Handler (1-click fixes) ──────────────────────────
  function handleBatchResolveIssues(fixes: Record<number, Record<string, unknown>>) {
    const nextOverrides = {
      ...overrides,
      ...fixes,
    };
    setOverrides(nextOverrides);

    previewMutation.mutate(
      {
        fileId: introspectResult?.fileId,
        file: introspectResult?.fileId ? undefined : selectedFile ?? undefined,
        sheetName: selectedSheet,
        headerRowIndex,
        columnMapping,
        defaultValues,
        overrides: nextOverrides,
      },
      {
        onSuccess: (res) => {
          if (res.success && res.data) {
            setPreviewResult(res.data);
            toast.success(`${String(Object.keys(fixes).length)} issues resolved automatically!`);
          }
        },
      },
    );
  }

  // ─── Step 4: Real Database Commit ──────────────────────────────────────────
  function handleConfirmImport() {
    if (!introspectResult && !selectedFile) return;

    setPhase('importing');

    importMutation.mutate(
      {
        fileId: introspectResult?.fileId,
        file: introspectResult?.fileId ? undefined : selectedFile ?? undefined,
        sheetName: selectedSheet,
        headerRowIndex,
        columnMapping,
        defaultValues,
        overrides,
        skipFailedRows: true,
      },
      {
        onSuccess: (res) => {
          if (res.success && res.data) {
            setCommitResult(res.data);
            setPhase('committed');
            toast.success(`Import complete! ${String(res.data.successCount)} policies successfully saved.`);
          } else if (res.error) {
            setErrorDetails(res.error.details ?? [res.error.message]);
            setPhase('error');
            toast.error(res.error.message);
          }
        },
        onError: (err) => {
          const axErr = err as { response?: { data?: { error?: { message?: string; details?: string[] } } }; message?: string };
          const srvErr = axErr.response?.data?.error;
          setErrorDetails(srvErr?.details ?? [srvErr?.message ?? axErr.message ?? 'Import commit failed']);
          setPhase('error');
        },
      },
    );
  }

  // ─── Export Report Helper ──────────────────────────────────────────────────
  function handleDownloadReport(resultToExport?: BulkImportResult | null) {
    const target = resultToExport ?? previewResult ?? commitResult;
    if (!target) return;

    const toastId = toast.loading('Generating Excel report...');
    bulkImportService
      .exportReport(target.rowStatuses)
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Import_Audit_${new Date().toISOString().slice(0, 10)}.xlsx`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success('Report downloaded', { id: toastId });
      })
      .catch(() => toast.error('Failed to generate report', { id: toastId }));
  }

  // ─── Preset Management ─────────────────────────────────────────────────────
  function handleSavePreset(name: string) {
    if (!introspectResult) return;
    const newPreset = savePreset(name, introspectResult.headers, columnMapping, defaultValues);
    setSavedPresets(getSavedPresets());
    setMatchedPreset(newPreset);
    toast.success(`Preset "${name}" saved! Future uploads matching this file layout will use it automatically.`);
  }

  function handleLoadPreset(preset: ImportMappingPreset) {
    setMatchedPreset(preset);
    setColumnMapping({
      ...INITIAL_MAPPING,
      ...preset.mapping,
      policyTypeValueMappings: preset.policyTypeValueMappings ?? preset.mapping.policyTypeValueMappings ?? {},
    });
    if (preset.defaultValues) {
      setDefaultValues({
        ...INITIAL_DEFAULTS,
        ...preset.defaultValues,
      });
    }
    toast.success(`Applied template: "${preset.name}"`);
  }

  function handleReset() {
    setPhase('upload');
    setSelectedFile(null);
    setIntrospectResult(null);
    setPreviewResult(null);
    setCommitResult(null);
    setOverrides({});
    setErrorDetails([]);
    setMatchedPreset(null);
    setColumnMapping(INITIAL_MAPPING);
    setDefaultValues(INITIAL_DEFAULTS);
  }

  // ─── Step progress indicators ──────────────────────────────────────────────
  const currentStepNumber = {
    upload: 1,
    introspecting: 1,
    mapping: 2,
    previewing: 3,
    review: 3,
    importing: 4,
    committed: 4,
    error: 1,
  }[phase];

  return (
    <AppShellPage
      icon={Upload}
      title="Universal Bulk Import"
      fullWidth={phase === 'review'}
      contentClassName={phase === 'review' ? 'w-full px-2 sm:px-4' : ''}
      actions={
        <Button
          variant="outline"
          size="sm"
          className="!hidden md:!flex items-center gap-1.5 font-bold shadow-xs"
          onClick={handleDownloadTemplate}
          leftIcon={<Download size={14} />}
        >
          Sample Template
        </Button>
      }
    >
      <div className={phase === 'review' ? 'space-y-3' : 'space-y-6'}>
        {/* Step Progress Tracker */}
        <div className="max-w-3xl mx-auto">
          <div className="grid grid-cols-4 gap-2 text-center text-xs select-none">
            <div
              className={cn(
                'py-2 px-1 rounded-xl border font-bold transition-all flex items-center justify-center gap-1.5',
                currentStepNumber >= 1
                  ? 'border-slate/40 bg-slate/10 text-ink shadow-2xs'
                  : 'border-line/40 text-ink-faint bg-surface/30',
              )}
            >
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-slate text-white text-[10px]">
                1
              </span>
              <span className="truncate hidden sm:inline">Upload File</span>
            </div>

            <div
              className={cn(
                'py-2 px-1 rounded-xl border font-bold transition-all flex items-center justify-center gap-1.5',
                currentStepNumber >= 2
                  ? 'border-slate/40 bg-slate/10 text-ink shadow-2xs'
                  : 'border-line/40 text-ink-faint bg-surface/30',
              )}
            >
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-slate text-white text-[10px]">
                2
              </span>
              <span className="truncate hidden sm:inline">Map Columns</span>
            </div>

            <div
              className={cn(
                'py-2 px-1 rounded-xl border font-bold transition-all flex items-center justify-center gap-1.5',
                currentStepNumber >= 3
                  ? 'border-slate/40 bg-slate/10 text-ink shadow-2xs'
                  : 'border-line/40 text-ink-faint bg-surface/30',
              )}
            >
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-slate text-white text-[10px]">
                3
              </span>
              <span className="truncate hidden sm:inline">Review &amp; Fix</span>
            </div>

            <div
              className={cn(
                'py-2 px-1 rounded-xl border font-bold transition-all flex items-center justify-center gap-1.5',
                currentStepNumber >= 4
                  ? 'border-green-500/40 bg-green-500/10 text-green-700 dark:text-green-300 shadow-2xs'
                  : 'border-line/40 text-ink-faint bg-surface/30',
              )}
            >
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-green-600 text-white text-[10px]">
                4
              </span>
              <span className="truncate hidden sm:inline">Completed</span>
            </div>
          </div>
        </div>

        {/* ─── Phase: Upload & Introspection ─────────────────────────────────── */}
        {(phase === 'upload' || phase === 'introspecting') && (
          <FileUploadZone
            onFileSelected={handleFileSelected}
            isIntrospecting={introspectMutation.isPending}
            introspectResult={introspectResult}
            selectedSheet={selectedSheet}
            onSheetChange={(s) => { setSelectedSheet(s); }}
            headerRowIndex={headerRowIndex}
            onHeaderRowIndexChange={(idx) => { setHeaderRowIndex(idx); }}
            onProceedToMapping={handleProceedToMapping}
            onDownloadTemplate={handleDownloadTemplate}
            matchedPreset={matchedPreset}
          />
        )}

        {/* ─── Phase: Column Mapping Wizard ──────────────────────────────────── */}
        {phase === 'mapping' && introspectResult && (
          <ColumnMappingWizard
            introspectResult={introspectResult}
            mapping={columnMapping}
            onMappingChange={(m) => { setColumnMapping(m); }}
            defaultValues={defaultValues}
            onDefaultValuesChange={(d) => { setDefaultValues(d); }}
            onProceedToPreview={handleRunPreview}
            onBackToUpload={() => { setPhase('upload'); }}
            savedPresets={savedPresets}
            onSavePreset={handleSavePreset}
            onLoadPreset={handleLoadPreset}
            isPreviewing={previewMutation.isPending}
          />
        )}

        {/* ─── Phase: In-Flight Validation (Previewing) ───────────────────────── */}
        {phase === 'previewing' && (
          <SurfaceCard className="p-12 text-center border border-line max-w-xl mx-auto space-y-4">
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-slate/10 dark:bg-slate/20 mx-auto">
              <Sparkles size={32} className="text-slate animate-pulse" />
            </div>
            <Loader2 size={24} className="mx-auto animate-spin text-slate" />
            <h3 className="text-sm font-bold text-ink">Analyzing &amp; Validating Records…</h3>
            <p className="text-xs text-ink-faint max-w-sm mx-auto">
              Aligning columns, checking for duplicate policies, standardizing dates, and resolving associate accounts.
            </p>
          </SurfaceCard>
        )}

        {/* ─── Phase: Interactive Data Review Grid ───────────────────────────── */}
        {phase === 'review' && previewResult && (
          <DataReviewGrid
            result={previewResult}
            fileName={introspectResult?.fileName ?? selectedFile?.name ?? 'Spreadsheet'}
            onConfirmImport={handleConfirmImport}
            onBackToMapping={() => { setPhase('mapping'); }}
            onDownloadReport={() => { handleDownloadReport(previewResult); }}
            onRowOverride={handleRowOverride}
            onRowMultiOverride={handleRowMultiOverride}
            onBatchResolveIssues={handleBatchResolveIssues}
            isImporting={importMutation.isPending}
          />
        )}

        {/* ─── Phase: In-Flight Database Commit ───────────────────────────────── */}
        {phase === 'importing' && (
          <SurfaceCard className="p-12 text-center border border-line max-w-xl mx-auto space-y-4">
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-green-500/10 text-green-600 dark:text-green-400 mx-auto">
              <Layers size={32} className="animate-bounce" />
            </div>
            <Loader2 size={24} className="mx-auto animate-spin text-green-600 dark:text-green-400" />
            <h3 className="text-sm font-bold text-ink">Saving to Database…</h3>
            <p className="text-xs text-ink-faint max-w-sm mx-auto">
              Creating client profiles, linking associate policies, and registering policies atomically.
            </p>
          </SurfaceCard>
        )}

        {/* ─── Phase: Committed Result Summary ────────────────────────────────── */}
        {phase === 'committed' && commitResult && (
          <ImportSummaryCard
            result={commitResult}
            onDownloadReport={() => { handleDownloadReport(commitResult); }}
            onImportAnother={handleReset}
          />
        )}

        {/* ─── Phase: Error Card ──────────────────────────────────────────────── */}
        {phase === 'error' && (
          <SurfaceCard className="p-8 border border-red-500/20 bg-red-500/5 max-w-xl mx-auto text-center space-y-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-red-500/10 text-red-500 mx-auto">
              <XCircle size={32} strokeWidth={1.5} />
            </div>
            <h3 className="text-sm font-bold text-ink">Spreadsheet Processing Error</h3>
            <p className="text-xs text-ink-faint">
              An issue occurred while reading or validating the spreadsheet. Review details below:
            </p>

            {errorDetails.length > 0 && (
              <div className="max-h-48 overflow-y-auto space-y-1.5 text-left p-3 rounded-xl bg-paper/60 border border-line">
                {errorDetails.map((err, i) => (
                  <p key={i} className="text-xs font-semibold text-red-600 dark:text-red-400 leading-tight">
                    &bull; {err}
                  </p>
                ))}
              </div>
            )}

            <div className="pt-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleReset}
                leftIcon={<RotateCcw size={13} />}
                className="font-bold text-xs"
              >
                Try Again / Choose Another File
              </Button>
            </div>
          </SurfaceCard>
        )}
      </div>
    </AppShellPage>
  );
}
